import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowDownward,
  ArrowUpward,
  ChevronRight,
  CloudDone,
  CloudUpload,
  DeleteOutline,
  DriveFileMove,
  Edit,
  FileDownload,
  Folder,
  Schema,
} from "@mui/icons-material";
import { ProcessModelFolderType, SelectedProcessModelType } from "model/processModel";
import dayjs from "dayjs";
import { processmodelsApi } from "api";
import { useSnackbar, useTranslations } from "hooks";
import { getTranslation } from "common";
import { useDispatch } from "react-redux";
import { deleteProcessModelById, updateProcessModel } from "Redux/processReducer";
import MoveToFolderMenu, {
  collectDescendantIds,
} from "./MoveToFolderMenu";

export type ProcessLibraryRow =
  | { kind: "folder"; folder: ProcessModelFolderType }
  | { kind: "model"; model: SelectedProcessModelType };

export type MoveTarget = { row: ProcessLibraryRow; targetFolderId: number | null };

interface ColumnOrderState {
  active: boolean;
  direction: "asc" | "desc" | null;
}

interface ProcessLibraryTableProps {
  rows: ProcessLibraryRow[];
  selectedRow: ProcessLibraryRow | null;
  isAdmin?: boolean;
  loading?: boolean;
  onOpenFolder: (folderId: number) => void;
  onSelectRow: (row: ProcessLibraryRow) => void;
  onEditModel: (modelId: number) => void;
  onExportModel: (model: SelectedProcessModelType) => void;
  onDeleteFolder: (folderId: number) => void;
  order: { name: ColumnOrderState; modified: ColumnOrderState };
  onToggleOrder: (column: "name" | "modified") => void;
  processModelFolders: ProcessModelFolderType[];
  onMove: (target: MoveTarget) => void;
}

const rowName = (row: ProcessLibraryRow) =>
  row.kind === "folder" ? row.folder.name : row.model.name;

const SortableHeaderCell: React.FC<{
  label: string;
  column: "name" | "modified";
  state: ColumnOrderState;
  onToggle: (column: "name" | "modified") => void;
}> = ({ label, column, state, onToggle }) => {
  const translations = useTranslations();
  const direction = state.direction;
  const ariaSort = !state.active || direction === null
    ? "none"
    : direction === "asc" ? "ascending" : "descending";
  const ariaLabel = `${getTranslation(translations, "process_library.sort_by")} ${label} (${
    !state.active || direction === null
      ? getTranslation(translations, "process_library.unsorted")
      : direction === "asc"
        ? getTranslation(translations, "process_library.ascending")
        : getTranslation(translations, "process_library.descending")
  })`;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      onClick={() => onToggle(column)}
      sx={{ cursor: "pointer", userSelect: "none" }}
      aria-label={ariaLabel}
      role="columnheader"
      aria-sort={ariaSort}
    >
      <Typography variant="inherit" fontWeight="inherit">{label}</Typography>
      {direction === "desc" ? (
        <ArrowUpward sx={{ fontSize: 16 }} />
      ) : (
        <ArrowDownward
          sx={{ fontSize: 16, color: !state.active || direction === null ? "text.disabled" : "inherit" }}
        />
      )}
    </Stack>
  );
};

const ProcessLibraryTable: React.FC<ProcessLibraryTableProps> = ({
  rows,
  selectedRow,
  isAdmin,
  loading,
  onOpenFolder,
  onSelectRow,
  onEditModel,
  onExportModel,
  onDeleteFolder,
  order,
  onToggleOrder,
  processModelFolders,
  onMove,
}) => {
  const [moveAnchor, setMoveAnchor] = useState<HTMLElement | null>(null);
  const [movingRow, setMovingRow] = useState<ProcessLibraryRow | null>(null);
  const [confirmDepublish, setConfirmDepublish] = useState<{
    model: SelectedProcessModelType;
    step: "first" | "second";
  } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    model: SelectedProcessModelType;
    step: "first" | "second";
  } | null>(null);
  const { sendSnack } = useSnackbar();
  const translations = useTranslations();
  const dispatch = useDispatch();

  const moveContext = useMemo(() => {
    if (!movingRow) return null;
    if (movingRow.kind === "folder") {
      const excluded = new Set<number>([movingRow.folder.id]);
      collectDescendantIds(movingRow.folder, excluded);
      return {
        excludeIds: excluded,
        currentParentId: movingRow.folder.idfolder ?? null,
      };
    }
    return {
      excludeIds: new Set<number>(),
      currentParentId: movingRow.model.idfolder ?? null,
    };
  }, [movingRow]);

  const handleMoveClick = (
    event: React.MouseEvent<HTMLElement>,
    row: ProcessLibraryRow
  ) => {
    event.stopPropagation();
    setMovingRow(row);
    setMoveAnchor(event.currentTarget);
  };

  const handleMoveClose = () => {
    setMoveAnchor(null);
    setMovingRow(null);
  };

  const handleMovePick = (targetFolderId: number | null) => {
    if (movingRow) {
      onMove({ row: movingRow, targetFolderId });
    }
  };

  // ponytail: same business logic as ProcessModel.handlePublish / handleDepublish
  // (GET → validate → POST publish; depublish goes through a two-step confirm).
  const handlePublish = async (model: SelectedProcessModelType) => {
    try {
      const { data: pmData } = await processmodelsApi.get(`/${model.id}`);
      await processmodelsApi.put(`/validate`, { ...pmData });
      const { data } = await processmodelsApi.post(`/${Number(model.id)}/publish`);
      sendSnack({ message: data.responsemessage, type: "success" });
      dispatch(updateProcessModel({ id: model.id, patch: { modeltype: 1 } }));
    } catch (error: any) {
    }
  };

  const handleDepublishClick = (model: SelectedProcessModelType) => {
    setConfirmDepublish({ model, step: "first" });
  };

  const deleteModel = async (model: SelectedProcessModelType) => {
    try {
      const { data } = await processmodelsApi.delete(`/${model.id}`);
      sendSnack({ message: data.responsemessage, type: "success" });
      dispatch(deleteProcessModelById({ id: model.id }));
    } catch (error: any) {
      sendSnack({ message: error.response?.data?.message, type: "error" });
    }
  };

  const confirmDeleteDialog = async () => {
    if (!confirmDelete) return;
    if (confirmDelete.step === "first") {
      setConfirmDelete({ model: confirmDelete.model, step: "second" });
      return;
    }
    const { model } = confirmDelete;
    setConfirmDelete(null);
    await deleteModel(model);
  };

  const confirmDepublishDialog = async () => {
    if (!confirmDepublish) {
      return;
    }
    if (confirmDepublish.step === "first") {
      setConfirmDepublish({ model: confirmDepublish.model, step: "second" });
      return;
    }
    const { model } = confirmDepublish;
    setConfirmDepublish(null);
    try {
      const { data } = await processmodelsApi.delete(`${model.id}/depublish`);
      sendSnack({ message: data.responsemessage, type: "success" });
      dispatch(updateProcessModel({ id: model.id, patch: { modeltype: 0 } }));
    } catch (error: any) {
    }
  };

  return (
    <TableContainer
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.paper",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" px={3} py={2}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Folder sx={{ color: "primary.main" }} />
          <Box>
            <Typography variant="h6">{getTranslation(translations, "menu.left.process")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {rows.length} {getTranslation(translations, "process_library.items")}
            </Typography>
          </Box>
        </Stack>
      </Stack>
      <Box flex={1} minHeight={0} sx={{ overflow: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <SortableHeaderCell
                  label={getTranslation(translations, "generic.name")}
                  column="name"
                  state={order.name}
                  onToggle={onToggleOrder}
                />
              </TableCell>
              <TableCell width={190}>
                <SortableHeaderCell
                  label={getTranslation(translations, "generic.edited")}
                  column="modified"
                  state={order.modified}
                  onToggle={onToggleOrder}
                />
              </TableCell>
              <TableCell width={130}>{getTranslation(translations, "generic.status")}</TableCell>
              <TableCell width={130}>{getTranslation(translations, "generic.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !rows.length ? (
              Array.from({ length: 6 }).map((_, index) => (
                <TableRow key={`skeleton-${index}`} sx={{ height: 56 }} data-testid="library-skeleton-row">
                  <TableCell>
                    <Skeleton variant="text" width="60%" height={24} />
                  </TableCell>
                  <TableCell width={190}>
                    <Skeleton variant="text" width="70%" height={24} />
                  </TableCell>
                  <TableCell width={130}>
                    <Skeleton variant="rounded" width={90} height={24} />
                  </TableCell>
                  <TableCell width={130}>
                    <Stack direction="row" spacing={0.5}>
                      <Skeleton variant="circular" width={24} height={24} />
                      <Skeleton variant="circular" width={24} height={24} />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              rows.map((row) => {
              const id = row.kind === "folder" ? `folder-${row.folder.id}` : `model-${row.model.id}`;
              const isSelected =
                selectedRow?.kind === row.kind &&
                (row.kind === "folder"
                  ? selectedRow.kind === "folder" && selectedRow.folder.id === row.folder.id
                  : selectedRow.kind === "model" && selectedRow.model.id === row.model.id);

              return (
                <TableRow
                  hover
                  key={id}
                  selected={isSelected}
                  onClick={() => onSelectRow(row)}
                  onDoubleClick={() =>
                    row.kind === "folder" ? onOpenFolder(row.folder.id) : onEditModel(row.model.id)
                  }
                  sx={{ cursor: "pointer", height: 56 }}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      {row.kind === "folder" ? (
                        <Folder sx={{ color: "primary.main" }} />
                      ) : (
                        <Schema sx={{ color: "primary.main" }} />
                      )}
                      <Typography fontWeight={600}>{rowName(row)}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {row.kind === "model" && row.model.creation
                      ? dayjs(row.model.creation).format("DD/MM/YYYY HH:mm")
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {row.kind === "folder" ? (
                      <Typography variant="body2" color="text.disabled">-</Typography>
                    ) : row.model.modeltype === 1 ? (
                      <Chip size="small" color="success" label={getTranslation(translations, "pm.button.published")} />
                    ) : (
                      <Chip size="small" variant="outlined" label={getTranslation(translations, "process_library.unpublished")} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={getTranslation(translations, "process_library.move_to_folder")}>
                        <IconButton size="small" onClick={(event) => handleMoveClick(event, row)}>
                          <DriveFileMove fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {row.kind === "model" && (
                        <React.Fragment>
                          <Tooltip title={getTranslation(translations, "process_library.edit_model")}>
                            <IconButton size="small" onClick={(event) => {
                              event.stopPropagation();
                              onEditModel(row.model.id);
                            }}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={getTranslation(translations, "pm.button.export_model")}>
                            <IconButton size="small" onClick={(event) => {
                              event.stopPropagation();
                              onExportModel(row.model);
                            }}>
                              <FileDownload fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {isAdmin && (
                            <Tooltip title={getTranslation(translations, "generic.button.delete")}>
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (row.model.modeltype === 1) {
                                    setConfirmDelete({ model: row.model, step: "first" });
                                  } else {
                                    deleteModel(row.model);
                                  }
                                }}
                                data-testid={`delete-model-btn-${row.model.id}`}
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isAdmin && (
                            row.model.modeltype === 1 ? (
                              <Tooltip title={getTranslation(translations, "pm.buttons.action.depublish")}>
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleDepublishClick(row.model);
                                  }}
                                  data-testid={`depublish-model-btn-${row.model.id}`}
                                >
                                  <CloudDone fontSize="small" color="primary" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title={getTranslation(translations, "pm.buttons.action.publish")}>
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handlePublish(row.model);
                                  }}
                                  data-testid={`publish-model-btn-${row.model.id}`}
                                >
                                  <CloudUpload fontSize="small" color="primary" />
                                </IconButton>
                              </Tooltip>
                            )
                          )}
                        </React.Fragment>
                      )}
                      {row.kind === "folder" && isAdmin && (
                        <Tooltip title={getTranslation(translations, "process_library.delete_folder")}>
                          <IconButton size="small" onClick={(event) => {
                            event.stopPropagation();
                            onDeleteFolder(row.folder.id);
                          }}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {row.kind === "folder" && (
                        <Tooltip title={getTranslation(translations, "process_library.open_folder")}>
                          <IconButton size="small" onClick={(event) => {
                            event.stopPropagation();
                            onOpenFolder(row.folder.id);
                          }}>
                            <ChevronRight fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            }))
            }
            {!rows.length && !loading && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Stack minHeight={220} alignItems="center" justifyContent="center">
                    <Typography color="text.secondary">{getTranslation(translations, "process_library.no_models_or_folders")}</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      {moveContext && movingRow && (
        <MoveToFolderMenu
          anchorEl={moveAnchor}
          onClose={handleMoveClose}
          folders={processModelFolders}
          excludeIds={moveContext.excludeIds}
          currentParentId={moveContext.currentParentId}
          onPick={handleMovePick}
        />
      )}
      <Dialog
        open={confirmDepublish?.step === "first"}
        onClose={() => setConfirmDepublish(null)}
      >
        <DialogTitle>{getTranslation(translations, "generic.warning.caption")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {getTranslation(translations, "pm.depublish.first.warning")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDepublish(null)} color="primary">
            {getTranslation(translations, "generic.no.caption")}
          </Button>
          <Button onClick={confirmDepublishDialog} color="primary">
            {getTranslation(translations, "generic.yes.caption")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmDelete?.step === "first"} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{getTranslation(translations, "generic.warning.caption")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {getTranslation(translations, "pm.delete.first.warning")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} color="primary">
            {getTranslation(translations, "generic.no.caption")}
          </Button>
          <Button onClick={confirmDeleteDialog} color="primary" data-testid="confirm-delete-model-btn">
            {getTranslation(translations, "generic.yes.caption")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmDelete?.step === "second"} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{getTranslation(translations, "generic.warning.caption")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {getTranslation(translations, "pm.depublish.second.warning")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} color="primary">
            {getTranslation(translations, "generic.no.caption")}
          </Button>
          <Button onClick={confirmDeleteDialog} color="primary" data-testid="death-model-btn">
            {getTranslation(translations, "generic.yes.caption")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmDepublish?.step === "second"}
        onClose={() => setConfirmDepublish(null)}
      >
        <DialogTitle>{getTranslation(translations, "generic.warning.caption")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {getTranslation(translations, "pm.depublish.second.warning")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDepublish(null)} color="primary">
            {getTranslation(translations, "generic.no.caption")}
          </Button>
          <Button onClick={confirmDepublishDialog} color="primary" data-testid="confirm-depublish-btn">
            {getTranslation(translations, "generic.yes.caption")}
          </Button>
        </DialogActions>
      </Dialog>
    </TableContainer>
  );
};

export default ProcessLibraryTable;
